import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
  OpportunityRelatedInclusion,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isAccommodationPackage } from '../../../../typeNarrowingUtils';

import { parseRoomNameFromDealName } from './dealOptionNameParser';
import { DEFAULT_CURRENCY, normaliseDealOptionText as normalise, parseDealOptionNumber } from './dealOptionsFormatters';

export interface DealOptionGroup {
  id: string;
  roomName: string;
  deals: Array<DealOptionRow>;
}

export interface DealOptionRow {
  id: string;
  allocation: string | number | null;
  bundledExperiences: Array<DealOptionBundledExperienceRow>;
  convertedCost: string | number | null;
  costNight: number | null;
  costPrice: string | number | null;
  currency: string;
  deal: OpportunityRelatedDeal;
  inclusions: Array<OpportunityRelatedInclusion>;
  marginCheck: string | null;
  marginPercent: string | number | null;
  maxExtraNights: string | number | null;
  name: string;
  roomRateIds: Array<string>;
  roomName: string;
  sellPrice: string | number | null;
}

export interface DealOptionBundledExperienceRow {
  id: string;
  ancillarySellPriceUplift: string | number | null;
  accountName: string;
  currency: string;
  dealOptionName: string;
  destination: string;
  fareType: string;
  listedInLinkHtml: string | null;
  name: string;
  subCategory: string;
  value: string | number | null;
}

export interface DealOptionsViewModel {
  groups: Array<DealOptionGroup>;
}

function stringifyId(value: string | number | null | undefined) {
  return value === null || value === undefined || value === '' ? undefined : String(value);
}

export function getDealKey(deal: OpportunityRelatedDeal) {
  return stringifyId(deal.sfid) ?? stringifyId(deal.id) ?? '';
}

function getPackageSalesforceId(offerPackage: App.Package) {
  return offerPackage.id_salesforce_external ? String(offerPackage.id_salesforce_external) : undefined;
}

function buildRoomRateIdsByDealId(packages: Array<App.Package>) {
  const roomRateIdsByDealId = new Map<string, Array<string>>();

  packages.forEach((offerPackage) => {
    if (!isAccommodationPackage(offerPackage)) {
      return;
    }

    const dealId = getPackageSalesforceId(offerPackage);
    if (!dealId || !offerPackage.fk_room_rate_id) {
      return;
    }

    roomRateIdsByDealId.set(dealId, [String(offerPackage.fk_room_rate_id)]);
  });

  return roomRateIdsByDealId;
}

function getRoomName(deal: OpportunityRelatedDeal) {
  return normalise(deal.room_asset?.name) ?? parseRoomNameFromDealName(deal.name) ?? 'Room not linked';
}

function getRoomGroupId(deal: OpportunityRelatedDeal, roomName: string) {
  return stringifyId(deal.room_asset?.sfid) ?? stringifyId(deal.room_asset?.id) ?? roomName.toLowerCase();
}

export function getNightCount(deal: OpportunityRelatedDeal) {
  const explicitNightCount = parseDealOptionNumber(deal.number_of_nights__c);
  if (explicitNightCount && explicitNightCount > 0) {
    return explicitNightCount;
  }

  const nameNightMatch = normalise(deal.name)?.match(/\b(\d+)\s*N\b/i);
  const parsedNameNightCount = nameNightMatch ? Number(nameNightMatch[1]) : undefined;

  return parsedNameNightCount && parsedNameNightCount > 0 ? parsedNameNightCount : undefined;
}

function getCostNight(deal: OpportunityRelatedDeal) {
  const nightCount = getNightCount(deal);
  const costPrice = parseDealOptionNumber(deal.cost_price__c);

  if (!nightCount || costPrice === undefined) {
    return null;
  }

  return costPrice / nightCount;
}

function getBundledExperienceRows(
  deal: OpportunityRelatedDeal,
  bundledExperiences: Array<OpportunityRelatedBundledExperience>,
): Array<DealOptionBundledExperienceRow> {
  const dealName = normalise(deal.name) ?? stringifyId(deal.id) ?? 'Deal option';

  return bundledExperiences.map((bundledExperience) => ({
    id: bundledExperience.id,
    accountName: normalise(bundledExperience.dealoption__account_name) ?? '',
    ancillarySellPriceUplift:
      bundledExperience.sell_price_uplift_number__c ?? bundledExperience.sell_price_uplift__c ?? null,
    currency: bundledExperience.dealoption__currencyisocode ?? '',
    dealOptionName: dealName,
    destination: normalise(bundledExperience.dealoption__destination) ?? '',
    fareType: normalise(bundledExperience.fare_type_display__c ?? bundledExperience.channel_fare_type__c) ?? '',
    listedInLinkHtml: bundledExperience.view_in_led__c ?? null,
    name: normalise(bundledExperience.name) ?? 'Bundled experience',
    subCategory: normalise(bundledExperience.dealoption__deal_sub_category) ?? '',
    value: bundledExperience.dealoption__value_cost_currency ?? null,
  }));
}

export function buildDealOptionsViewModel({
  bundledExperiences = [],
  deals,
  inclusions,
  packages = [],
}: {
  bundledExperiences?: Array<OpportunityRelatedBundledExperience>;
  deals: Array<OpportunityRelatedDeal>;
  inclusions: Array<OpportunityRelatedInclusion>;
  packages?: Array<App.Package>;
}): DealOptionsViewModel {
  const inclusionsByDealId = new Map<string, Array<OpportunityRelatedInclusion>>();
  const bundledExperiencesByDealId = new Map<string, Array<OpportunityRelatedBundledExperience>>();
  const roomRateIdsByDealId = buildRoomRateIdsByDealId(packages);

  for (const inclusion of inclusions) {
    const dealId = normalise(inclusion.deal_offer__c);
    if (!dealId) {
      continue;
    }

    inclusionsByDealId.set(dealId, [...(inclusionsByDealId.get(dealId) ?? []), inclusion]);
  }

  for (const bundledExperience of bundledExperiences) {
    const dealId = normalise(bundledExperience.deal_option__c);
    if (!dealId) {
      continue;
    }

    bundledExperiencesByDealId.set(dealId, [...(bundledExperiencesByDealId.get(dealId) ?? []), bundledExperience]);
  }

  const groupMap = new Map<string, DealOptionGroup>();

  for (const deal of deals) {
    const roomName = getRoomName(deal);
    const groupId = getRoomGroupId(deal, roomName);
    const dealKey = getDealKey(deal);
    const group = groupMap.get(groupId) ?? { id: groupId, roomName, deals: [] };

    group.deals.push({
      id: dealKey || `deal:${group.deals.length}`,
      allocation: deal.allocation__c ?? null,
      bundledExperiences: getBundledExperienceRows(deal, bundledExperiencesByDealId.get(dealKey) ?? []),
      convertedCost: deal.converted_cost__c ?? deal.cost_price_currency_price__c ?? null,
      costNight: getCostNight(deal),
      costPrice: deal.cost_price__c ?? null,
      currency: deal.currencyisocode ?? DEFAULT_CURRENCY,
      deal,
      inclusions: inclusionsByDealId.get(dealKey) ?? [],
      marginCheck: deal.margin_check__c ?? null,
      marginPercent: deal.margin_percent__c ?? null,
      maxExtraNights: deal.max_extra_nights__c ?? null,
      name: normalise(deal.name) ?? dealKey ?? 'Unnamed deal option',
      roomRateIds: roomRateIdsByDealId.get(dealKey) ?? [],
      roomName,
      sellPrice: deal.price__c ?? null,
    });

    groupMap.set(groupId, group);
  }

  return {
    groups: Array.from(groupMap.values()).sort((leftGroup, rightGroup) =>
      leftGroup.roomName.localeCompare(rightGroup.roomName),
    ),
  };
}
